import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

/**
 * A spend reward: an order over `minOrderTotal` (before tax, gift cards left
 * out) between earnFrom and earnTo gets a printed code worth `rewardAmount`
 * off a later order between redeemFrom and redeemTo. One per order. Lives in
 * pos-backend, offered only where a super admin has turned store credit on.
 * Dates are ISO instants.
 */
export interface SpendReward {
  id: string;
  name: string;
  minOrderTotal: number;
  rewardAmount: number;
  earnFrom: string;
  earnTo: string;
  redeemFrom: string;
  redeemTo: string;
  locationIds: string[];
  /** Off: paused, no new codes are printed. Codes already printed still work. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Codes printed for paid orders that met the amount. */
  issued: number;
  redeemed: number;
  /** Printed, never used, and past their use dates. */
  expired: number;
  /** Money taken off orders by the codes used. */
  discountGiven: number;
}

export interface SpendRewardInput {
  name: string;
  minOrderTotal: number;
  rewardAmount: number;
  earnFrom: string;
  earnTo: string;
  redeemFrom: string;
  redeemTo: string;
  locationIds: string[];
}

const KEY = ['spend-rewards'] as const;

/** Rewards offered at one location. */
export function useSpendRewards(locationId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, locationId ?? ''],
    queryFn: () =>
      posBackendApi.get<{ data: SpendReward[] }>('/spend-rewards', { locationId: locationId ?? undefined }),
    enabled: !!locationId,
    staleTime: 30_000,
  });
}

export function useCreateSpendReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SpendRewardInput) => posBackendApi.post<{ data: SpendReward }>('/spend-rewards', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateSpendReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SpendRewardInput }) =>
      posBackendApi.put<{ data: SpendReward }>(`/spend-rewards/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Pause (no new codes) or resume a reward. */
export function useSetSpendRewardActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      posBackendApi.post<{ data: SpendReward }>(`/spend-rewards/${id}/active`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
